/* IDRESSENCE — motion layer + interaction wiring.
   Every timing/easing value below is a token from tokens/motion.css, reproduced here only because CSS
   custom properties cannot be read directly inside a computed IntersectionObserver callback the way the
   .dc.html reference's inline React did — the NUMBERS are copied verbatim from README "Interactions &
   Behavior" and are not new values. prefers-reduced-motion is checked explicitly wherever this file drives
   motion by itself (the reveal observer, the reduced-motion video removal); CSS-only motion already zeroes
   itself via tokens/motion.css's own media query.

   Every init function below accepts an optional `root` (defaults to `document`) and is idempotent per
   element (guarded via a `data-*-bound` marker) so it is safe to call again after the Shopify Theme Editor
   hot-reloads a section (`shopify:section:load`) without double-binding listeners elsewhere on the page. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Shared by the quick-view modal and the mobile nav panel — both are dialogs and
  // both need the same trap, so the selector and the trap live in one place.
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocusWithin(container, e) {
    if (e.key !== 'Tab') return;
    var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE));
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ===================================================================
     Reduced motion: remove elements marked for full removal (video slots)
     =================================================================== */
  function stripReducedMotionMedia(root) {
    if (!reduceMotion) return;
    (root || document).querySelectorAll('[data-reduced-motion-remove]').forEach(function (el) {
      el.remove();
    });
  }

  /* ===================================================================
     Scroll reveal — README "Scroll reveal" spec, verbatim values
     =================================================================== */
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    var EASE = 'cubic-bezier(.22,.61,.36,1)';
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var siblings = el.parentNode ? Array.prototype.slice.call(el.parentNode.children) : [];
          var idx = siblings.indexOf(el);
          var delay = Math.min(idx, 5) * 80;
          el.style.setProperty('--reveal-delay', delay + 'ms');
          el.classList.add('is-revealed');
          revealObserver.unobserve(el);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -4% 0px' }
    );
    return revealObserver;
  }

  function initReveal(root) {
    if (reduceMotion) return; // elements are already visible via the CSS reduced-motion fallback
    var io = getRevealObserver();
    var scope = root || document;

    function scan() {
      scope.querySelectorAll('[data-reveal]:not([data-revealed])').forEach(function (el) {
        el.setAttribute('data-revealed', '1');
        io.observe(el);
        el.querySelectorAll('img').forEach(function (img) {
          if (!img.complete) img.addEventListener('load', function () { io.observe(el); }, { once: true });
        });
      });
    }

    scan();
    [300, 900, 2000].forEach(function (ms) { setTimeout(scan, ms); });
  }

  /* ===================================================================
     Header: scrolled state (40px threshold) + homepage scroll-progress bar
     Site-wide singleton — guarded so a header-group reload can't double-bind the scroll listener.
     =================================================================== */
  function initHeaderScroll() {
    var header = document.querySelector('[data-site-header]');
    var progressFill = document.querySelector('[data-progress-fill]');
    if (!header && !progressFill) return;
    if (header && header.hasAttribute('data-scroll-bound')) return;
    if (header) header.setAttribute('data-scroll-bound', '1');

    var scrolled = false;
    function onScroll() {
      var el = document.scrollingElement || document.documentElement;
      var isScrolled = el.scrollTop > 40;
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        if (header) header.classList.toggle('is-scrolled', scrolled);
      }
      if (progressFill) {
        var max = el.scrollHeight - el.clientHeight;
        var pct = max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 0;
        progressFill.style.width = pct + '%';
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ===================================================================
     FormatSwitch — global (homepage price-label toggle) + product (PDP buy box)
     =================================================================== */
  function setGlobalFormat(value) {
    document.querySelectorAll('[data-price-oil]').forEach(function (el) { el.hidden = value !== 'oil'; });
    document.querySelectorAll('[data-price-spray]').forEach(function (el) { el.hidden = value !== 'spray'; });
  }

  function setProductFormat(switchEl, value) {
    var section = switchEl.closest('section') || document;
    var idInput = section.querySelector('[data-variant-id-input]');
    var priceLabel = section.querySelector('[data-price-label]');
    var volumeLabel = section.querySelector('[data-volume-label]');
    var addBtn = section.querySelector('[data-add-to-bag]');

    var variantId = switchEl.getAttribute('data-' + value + '-variant-id');
    var available = switchEl.getAttribute('data-' + value + '-available') === 'true';

    if (!variantId) return; // that Format variant doesn't exist for this product — leave state as-is

    if (idInput) idInput.value = variantId;
    var volumeText = switchEl.getAttribute('data-' + value + '-volume-label');
    if (volumeLabel && volumeText) volumeLabel.textContent = volumeText;

    var price = switchEl.getAttribute('data-' + value + '-price-label');
    if (priceLabel && price) priceLabel.textContent = price;
    if (addBtn) {
      addBtn.disabled = !available;
      var idle = addBtn.getAttribute('data-idle-label');
      if (idle && price) {
        var newIdle = idle.replace(/—.*/, '— ' + price);
        addBtn.setAttribute('data-idle-label', newIdle);
        addBtn.textContent = newIdle;
        addBtn.removeAttribute('data-bagged');
      }
    }
  }

  function initFormatSwitches(root) {
    (root || document).querySelectorAll('[data-format-switch]:not([data-bound])').forEach(function (switchEl) {
      switchEl.setAttribute('data-bound', '1');
      switchEl.querySelectorAll('.idr-format-switch__tab').forEach(function (tab) {
        tab.addEventListener('click', function () {
          var value = tab.getAttribute('data-format-value');
          switchEl.querySelectorAll('.idr-format-switch__tab').forEach(function (t) {
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
          });
          if (switchEl.getAttribute('data-scope') === 'product') {
            setProductFormat(switchEl, value);
          } else {
            setGlobalFormat(value);
          }
        });
      });
    });
  }

  /* ===================================================================
     PDP gallery: thumbnail cross-fade + roving tabindex + arrow keys
     =================================================================== */
  function initGallery(root) {
    var scope = root || document;
    var stage = scope.querySelector('[data-gallery-stage]:not([data-bound])');
    var thumbWrap = scope.querySelector('[data-gallery-thumbs]:not([data-bound])');
    if (!stage || !thumbWrap) return;
    stage.setAttribute('data-bound', '1');
    thumbWrap.setAttribute('data-bound', '1');

    var thumbs = Array.prototype.slice.call(thumbWrap.querySelectorAll('[data-gallery-thumb]'));
    var images = Array.prototype.slice.call(stage.querySelectorAll('[data-gallery-image]'));
    if (!thumbs.length || !images.length) return;

    function activate(index) {
      thumbs.forEach(function (t, i) {
        var isActive = i === index;
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      images.forEach(function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(i); t.focus(); });
    });

    thumbWrap.addEventListener('keydown', function (e) {
      var current = thumbs.findIndex(function (t) { return t.getAttribute('tabindex') === '0'; });
      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % thumbs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + thumbs.length) % thumbs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = thumbs.length - 1;
      if (next !== null) {
        e.preventDefault();
        activate(next);
        thumbs[next].focus();
      }
    });
  }

  /* ===================================================================
     Quick-view modal — a11y additions the prototype omitted (focus trap, Escape,
     aria-modal, body scroll lock, focus return). Populated from a <template> that
     was fully server-rendered by product-card.liquid — no client-side markup building.
     Bound once per scrim instance; triggers are (re-)bound per call so cards added by a
     section reload still open the modal, without double-binding cards that already work.
     =================================================================== */
  var quickViewState = null; // { scrim, panel } — the one homepage-only modal instance

  function bindQuickViewScrim(scrim, panel) {
    if (scrim.hasAttribute('data-bound')) return;
    scrim.setAttribute('data-bound', '1');

    var lastTrigger = null;

    function onKeydown(e) {
      if (e.key === 'Escape') close();
      else trapFocusWithin(panel, e);
    }

    function open(handle, trigger) {
      var tpl = document.querySelector('template[data-quickview-content="' + handle + '"]');
      if (!tpl) return;
      panel.innerHTML = '';
      panel.appendChild(tpl.content.cloneNode(true));

      var closeBtn = panel.querySelector('[data-modal-close]');
      if (closeBtn) closeBtn.addEventListener('click', close);

      lastTrigger = trigger || null;
      scrim.hidden = false;
      scrim.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown, true);

      var focusable = panel.querySelector(FOCUSABLE);
      if (focusable) focusable.focus();
    }

    function close() {
      scrim.classList.remove('is-open');
      scrim.hidden = true;
      panel.innerHTML = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown, true);
      if (lastTrigger) lastTrigger.focus();
    }

    scrim.addEventListener('click', function (e) {
      if (e.target === scrim) close();
    });

    quickViewState = { open: open, close: close };
  }

  function initQuickView(root) {
    var scope = root || document;

    // Below the layout breakpoint the quick view is worse than the thing it replaces:
    // it covers a phone screen entirely to show less than the product page does, and it
    // intercepts a tap on a link that already goes somewhere better. Leaving the triggers
    // unbound lets the card's own <a href> do its job.
    if (window.matchMedia('(max-width: 900px)').matches) return;

    var scrim = document.querySelector('[data-modal-scrim]');
    var panel = document.querySelector('[data-modal-panel]');
    if (scrim && panel) bindQuickViewScrim(scrim, panel);
    if (!quickViewState) return; // no modal on this page (e.g. PDP) — nothing to trigger

    scope.querySelectorAll('[data-quickview]:not([data-bound])').forEach(function (trigger) {
      trigger.setAttribute('data-bound', '1');
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        quickViewState.open(trigger.getAttribute('data-quickview'), trigger);
      });
    });
  }

  /* ===================================================================
     Mobile navigation panel — the small-screen home of the header's nav links.
     Site-wide singleton, guarded the same way as the scroll listener so a
     header-group reload in the theme editor cannot double-bind it.
     =================================================================== */
  function initMobileNav() {
    var openBtn = document.querySelector('[data-nav-open]');
    var panel = document.querySelector('[data-nav-panel]');
    if (!openBtn || !panel) return;
    if (openBtn.hasAttribute('data-nav-bound')) return;
    openBtn.setAttribute('data-nav-bound', '1');

    var closeBtn = panel.querySelector('[data-nav-close]');
    var desktop = window.matchMedia('(min-width: 901px)');

    function onKeydown(e) {
      if (e.key === 'Escape') close();
      else trapFocusWithin(panel, e);
    }

    function open() {
      panel.hidden = false;
      // Two frames: the element must be laid out as visible before the opacity
      // transition has anything to transition from.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { panel.classList.add('is-open'); });
      });
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown, true);
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    function close() {
      if (panel.hidden) return;
      panel.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown, true);
      // 480ms is --dur-base; the panel must stay in the tree until it has faded.
      if (reduceMotion) panel.hidden = true;
      else setTimeout(function () { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 480);
      openBtn.focus();
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Following a link inside the panel leaves it open behind an in-page anchor,
    // and leaves the scroll lock on with it.
    panel.querySelectorAll('a[href]').forEach(function (a) {
      a.addEventListener('click', close);
    });

    // Rotating to landscape past the breakpoint would otherwise strand the page
    // with a hidden panel and a locked body.
    var onDesktop = function (e) { if (e.matches) close(); };
    if (desktop.addEventListener) desktop.addEventListener('change', onDesktop);
    else if (desktop.addListener) desktop.addListener(onDesktop); // Safari < 14
  }

  /* ===================================================================
     Add to bag — real Shopify AJAX cart mutation (/cart/add.js), pending + error state.
     No cart-drawer UI exists in the handoff to build against (out of scope per README's
     own "open questions" list) — success updates the header cart count and swaps the
     button label optimistically-then-confirmed; errors surface inline, nothing silent.
     =================================================================== */
  function initAddToBag(root) {
    (root || document).querySelectorAll('[data-add-to-bag]').forEach(function (btn) {
      var form = btn.closest('form');
      if (!form || form.hasAttribute('data-bag-bound')) return;
      form.setAttribute('data-bag-bound', '1');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = form.querySelector('[data-add-to-bag]');
        var idInput = form.querySelector('[data-variant-id-input]');
        if (!submitBtn || !idInput || !idInput.value) return;

        var idleLabel = submitBtn.getAttribute('data-idle-label') || submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Wird hinzugefügt …';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: idInput.value, quantity: 1 })
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (data) { throw new Error(data.description || 'Fehler beim Hinzufügen.'); });
            return res.json();
          })
          .then(function () {
            submitBtn.textContent = idleLabel.replace('In den Warenkorb', 'Im Warenkorb');
            submitBtn.setAttribute('data-bagged', '1');
            submitBtn.disabled = false;
            return fetch('/cart.js').then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            document.querySelectorAll('[data-cart-count]').forEach(function (el) {
              el.textContent = (el.getAttribute('data-cart-label') || 'Warenkorb') + ' (' + cart.item_count + ')';
            });
          })
          .catch(function (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = idleLabel;
            var errorEl = form.querySelector('[data-form-error]');
            if (!errorEl) {
              errorEl = document.createElement('p');
              errorEl.setAttribute('data-form-error', '');
              errorEl.setAttribute('role', 'alert');
              errorEl.style.cssText = 'font-family:var(--font-interface);font-size:12px;color:var(--oxblood-400);margin:8px 0 0;';
              form.appendChild(errorEl);
            }
            errorEl.textContent = err.message || 'Fehler beim Hinzufügen zum Warenkorb.';
          });
      });
    });
  }

  /* ===================================================================
     Boot + Shopify Theme Editor hot-reload support
     =================================================================== */
  function initAll(root) {
    stripReducedMotionMedia(root);
    initReveal(root);
    initHeaderScroll();
    initMobileNav();
    initFormatSwitches(root);
    initGallery(root);
    initQuickView(root);
    initAddToBag(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Shopify Theme Editor re-renders one section's markup via AJAX on every settings change —
  // without this, FormatSwitch/Gallery/Modal/Add-to-bag/Reveal would go dead the moment a
  // merchant edits that section, until a full page reload. Scoped to the reloaded section only,
  // so unrelated parts of the page are never re-bound.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
